import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import {
  Autocomplete,
  Box,
  FormControl,
  type InputBaseComponentProps,
  TextField,
} from '@mui/material';
import { useDebounce } from '@uidotdev/usehooks';
import _ from 'lodash';

import { transformToLabel } from '../../utils/helpers';

type Option = {
  label: string;
  value: string;
};

type FormAutoCompleteProps = {
  label?: string;
  name: string;
  options: Option[] | [];
  defaultValue?: string | number;
  isError?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  type?: 'text' | 'number';
  disabled?: boolean;
  style?: React.CSSProperties;
  inputProps?: InputBaseComponentProps;
};

const FormAutoComplete = ({
  label,
  defaultValue,
  name,
  options,
  style,
  disabled,
  fullWidth = true,
  ...props
}: FormAutoCompleteProps) => {
  const [value, setValue] = React.useState<string | null>(
    defaultValue?.toString() ?? options[0]?.label
  );

  const [inputValue, setInputValue] = React.useState(
    defaultValue?.toString() ?? options[0]?.label
  );

  const debouncedInputValue = useDebounce(inputValue, 300);

  const { formState, setValue: setFormValue } = useFormContext();

  const { errors } = formState;

  const errorMessage = errors[name]?.message as string | undefined;

  useEffect(() => {
    setFormValue(
      name,
      options.find((o) => o.label === debouncedInputValue)?.value ??
        debouncedInputValue,
      {
        shouldDirty: true,
      }
    );
  }, [value, debouncedInputValue, _.isEqual(inputValue, value)]);

  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue.toString());
      setInputValue(defaultValue.toString());
    }
  }, [defaultValue]);

  return (
    <FormControl
      {...props}
      style={{
        justifyContent: 'flex-end',
        ...style,
      }}
      fullWidth={fullWidth}
    >
      {label && (
        <label htmlFor={name} className="min-h-7 pb-2 text-sm text-[#666]">
          {transformToLabel(label)}
        </label>
      )}
      <Autocomplete
        id={name}
        options={options.map((option) => option.label)}
        size="small"
        value={value}
        disabled={disabled}
        freeSolo
        onChange={(event: any, newValue: string | null) => setValue(newValue)}
        inputValue={inputValue}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        sx={{
          '& .MuiAutocomplete-endAdornment': {
            display: 'none',
          },
        }}
        autoHighlight
        getOptionLabel={(option) => option}
        renderOption={(props, option) => {
          const { ...optionProps } = props;

          return (
            <Box component="li" {...optionProps} key={option}>
              {option}
            </Box>
          );
        }}
        renderInput={(params) => (
          <TextField
            defaultValue={defaultValue}
            {...params}
            fullWidth
            size="small"
            name={name}
            type={props.type}
            inputProps={{
              ...params.inputProps,
              ...props.inputProps,
            }}
          />
        )}
      />
      <div className="flex justify-between">
        {errorMessage && (
          <p className="w-full pt-2 text-start text-[14px] text-[#FF816A]">
            {errorMessage}
          </p>
        )}
      </div>
    </FormControl>
  );
};

export default FormAutoComplete;
